import { LOCALES, type Locale } from '@indusketch/shared';

export { LOCALES, type Locale };

/** Name of each language in that language, for the switcher. */
export const LOCALE_NAMES: Record<Locale, string> = { de: 'Deutsch', en: 'English', pl: 'Polski' };

/** BCP 47 tags for `Intl` (numbers, dates, plural rules). */
export const INTL_TAG: Record<Locale, string> = { de: 'de-DE', en: 'en-GB', pl: 'pl-PL' };

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

/**
 * Plural forms following `Intl.PluralRules` categories. German and English only need `one`
 * and `other`; Polish also distinguishes `few` (2–4, 22–24 …) and `many` (5–21, 25 …).
 */
export interface Plural {
  readonly one: string;
  readonly few?: string;
  readonly many?: string;
  readonly other: string;
}

export type Params = Readonly<Record<string, string | number>>;

/** Replaces `{name}` placeholders; unknown ones stay visible so a missing value is noticed. */
export function fmt(template: string, params: Params = {}): string {
  return template.replace(/\{(\w+)\}/g, (all, key: string) => {
    const value = params[key];
    return value === undefined ? all : String(value);
  });
}

const pluralRules = new Map<Locale, Intl.PluralRules>();

/** Picks the form for `count` and fills `{n}` plus further placeholders. */
export function plural(locale: Locale, forms: Plural, count: number, params: Params = {}): string {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(INTL_TAG[locale]);
    pluralRules.set(locale, rules);
  }
  const category = rules.select(count);
  const template =
    (category === 'one'
      ? forms.one
      : category === 'few'
        ? forms.few
        : category === 'many'
          ? forms.many
          : undefined) ?? forms.other;
  return fmt(template, { n: count, ...params });
}

/** First supported language among the browser's preferences, else German. */
export function detectLocale(preferred: readonly string[]): Locale {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return 'de';
}

export function formatDateTime(locale: Locale, date: Date | string): string {
  return new Intl.DateTimeFormat(INTL_TAG[locale], {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(typeof date === 'string' ? new Date(date) : date);
}

export function formatDate(locale: Locale, date: Date | string): string {
  return new Intl.DateTimeFormat(INTL_TAG[locale], { dateStyle: 'medium' }).format(
    typeof date === 'string' ? new Date(date) : date,
  );
}
